"use client";

import { useState } from "react";
import { GripVertical, List, Palette, Plus, SlidersHorizontal, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    TabItem,
    TabsAppearance,
    TabsBlock,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import {
    resolveTabsAppearance,
    TABS_CONFIG,
    createTabItem,
} from "../../lib/blockRegistry";
import { DEFAULT_THEME } from "../../lib/theme";
import { TabIcon } from "../../lib/tabIcons";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

export default function TabsSettings({ block }: { block: TabsBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const { items } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolveTabsAppearance(block.data.appearance, theme);
    const update = (patch: Partial<TabsBlock["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const setItems = (next: TabItem[]) => update({ items: next });

    // Second tier: clicking a tab focuses it (same store field the quiz and
    // flip cards use), and its own appearance appears here.
    const focusedTabId = useLomdaV3Editor((s) => s.focusedQuestionId);
    const focusedTab = items.find((it) => it.id === focusedTabId) ?? null;
    const patchTab = (patch: Partial<TabItem>) =>
        setItems(
            items.map((it) =>
                it.id === focusedTabId ? { ...it, ...patch } : it
            )
        );

    return (
        <div className="flex items-center gap-3">
            <span className="shrink-0 text-sm !font-semibold text-foreground">
                {t("lomda-v3-editor.interactive.tabs")}
            </span>
            <WidthControl blockId={block.id} />
            <AppearancePopover
                appearance={appearance}
                onChange={(patch) =>
                    update({ appearance: { ...appearance, ...patch } })
                }
                onReset={() => update({ appearance: undefined })}
            />
            <ManageTabsPopover items={items} onChange={setItems} />
            {focusedTab && (
                <>
                    <ToolbarDivider />
                    <TabAppearancePopover
                        tab={focusedTab}
                        onChange={(appearancePatch) =>
                            patchTab({
                                appearance: {
                                    ...focusedTab.appearance,
                                    ...appearancePatch,
                                },
                            })
                        }
                        onReset={() => patchTab({ appearance: undefined })}
                    />
                </>
            )}
        </div>
    );
}

/** Per-tab overrides — currently just this tab's text colour. */
function TabAppearancePopover({
    tab,
    onChange,
    onReset,
}: {
    tab: TabItem;
    onChange: (patch: Partial<NonNullable<TabItem["appearance"]>>) => void;
    onReset: () => void;
}) {
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const textDefault = theme?.foreground ?? DEFAULT_THEME.foreground;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen, "gap-1.5 px-3")}
                >
                    <Palette className="size-5" />
                    <span className="text-sm whitespace-nowrap">
                        {t("lomda-v3-editor.tabs.tab-appearance")}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.tabs.tab-appearance")}
                    </PopoverTitle>

                    <AppearanceRow label={t("lomda-v3-editor.format.color")}>
                        <ColorChipField
                            value={tab.appearance?.textColor ?? textDefault}
                            onChange={(c) => onChange({ textColor: c })}
                        />
                    </AppearanceRow>

                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}

function ManageTabsPopover({
    items,
    onChange,
}: {
    items: TabItem[];
    onChange: (items: TabItem[]) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...items];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <List className="size-5" />
                    {t("lomda-v3-editor.tabs.manage-tabs")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[352px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.tabs.manage-tabs")}
                    </PopoverTitle>
                    {items.map((it, i) => (
                        <div
                            key={it.id}
                            draggable
                            onDragStart={() => setDragIndex(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIndex !== null) move(dragIndex, i);
                                setDragIndex(null);
                            }}
                            className="flex items-center gap-2.5"
                        >
                            <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                            {it.icon && (
                                <TabIcon
                                    name={it.icon}
                                    className="size-4 shrink-0 text-muted-foreground"
                                />
                            )}
                            <input
                                value={it.label}
                                onChange={(e) =>
                                    onChange(
                                        items.map((x) =>
                                            x.id === it.id
                                                ? { ...x, label: e.target.value }
                                                : x
                                        )
                                    )
                                }
                                placeholder={t(
                                    "lomda-v3-editor.tabs.tab-label",
                                    undefined,
                                    { number: i + 1 }
                                )}
                                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                            />
                            <button
                                type="button"
                                disabled={items.length <= 1}
                                onClick={() =>
                                    onChange(items.filter((x) => x.id !== it.id))
                                }
                                className="shrink-0 text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                            >
                                <Trash2 className="size-5" />
                            </button>
                        </div>
                    ))}
                    <span className="h-px w-full bg-muted" />
                    <button
                        type="button"
                        onClick={() => onChange([...items, createTabItem()])}
                        className={cn(chromeBtn, "h-10 self-start")}
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.tabs.add")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function AppearancePopover({
    appearance,
    onChange,
    onReset,
}: {
    appearance: TabsAppearance;
    onChange: (patch: Partial<TabsAppearance>) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.tabs.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.tabs.appearance")}
                    </PopoverTitle>
                    {TABS_CONFIG.appearanceGroups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                const apKey =
                                    field.key as keyof TabsAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "switch" ? (
                                            <Switch
                                                checked={Boolean(value)}
                                                onCheckedChange={(v) =>
                                                    onChange({ [apKey]: v })
                                                }
                                            />
                                        ) : field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}
                    <AppearanceReset
                        onReset={onReset}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
